import { ThunkAction } from 'redux-thunk';
import * as R from 'ramda';

import Api from '../../../common/services/api';
import fetchUser from './fetch-user';

import type { StoreState } from '@common/types/store';
import type {
  ExtendedUser,
  FunctionLink,
  ManagerSource,
  Network,
  NetworkScope,
  Role,
  SimpleUser,
} from '@common/types/objects';
import Storage from '../../../common/services/storage';

export const CORE_ACCEPT_DOCUMENT = 'core/ACCEPT_DOCUMENT';
export const CLEAR_NOTIFICATION = 'core/CLEAR_NOTIFICATION';
export const CLEAR_NOTIFICATIONS = 'core/CLEAR_NOTIFICATIONS';
export const CLEAR_UNREAD = 'core/CLEAR_UNREAD';
export const RECEIVE_FILE = 'core/RECEIVE_FILE';
export const NETWORK_RECEIVE_USER_FUNCTIONS = 'core/RECEIVE_USER_FUNCTIONS';
export const RECEIVE_NOTIFICATIONS = 'core/RECEIVE_NOTIFICATIONS';
export const RECEIVE_PROFILE = 'core/RECEIVE_PROFILE';
export const CORE_RECEIVE_USER = 'core/RECEIVE_USER';
export const CORE_INITIALISE = 'core/INITIALISE';
export const CORE_INTEGRATION_AUTHENTICATED = 'core/INTEGRATION_AUTHENTICATED';
export const RECEIVE_NOTIFICATION = 'core/RECEIVE_NOTIFICATION';
export const UPDATE_PROFILE = 'core/UPDATE_PROFILE';
export const CORE_RECEIVE_LANGUAGES = 'core/RECEIVE_LANGUAGES';
export const CORE_REFRESH_REDEEM_CODE = 'core/REFRESH_REDEEM_CODE';
export const CORE_DISMISS = 'core/DISMISS';
export const SET_USER_STATUS = 'core/SET_USER_STATUS';
export const UNSET_USER_STATUS = 'core/UNSET_USER_STATUS';
export const SHOW_PLAN_PACKAGE_UPGRADE_MODAL = 'core/SHOW_PLAN_PACKAGE_UPGRADE_MODAL';
export const HIDE_PLAN_PACKAGE_UPGRADE_MODAL = 'core/HIDE_PLAN_PACKAGE_UPGRADE_MODAL';
export const CORE_ADD_MENTIONED_USER = 'core/ADD_MENTIONED_USER';
export const CORE_ADD_RELATED_USERS = 'core/ADD_RELATED_USERS';
export const CHATBOT_SETTINGS_UPDATED = 'core/CHATBOT_SETTINGS_UPDATED';

export type ChatbotSettingsUpdatedPayload = {
  isActive: boolean;
  name: string;
  icon: string | null;
};

export type ChatbotSettingsUpdatedAction = {
  type: typeof CHATBOT_SETTINGS_UPDATED;
  chatbot: ChatbotSettingsUpdatedPayload;
};

export const updateChatbotSettingsInStore = (
  chatbot: ChatbotSettingsUpdatedPayload
): ChatbotSettingsUpdatedAction => ({
  type: CHATBOT_SETTINGS_UPDATED,
  chatbot,
});

export type SetUserStatusAction = {
  type: typeof SET_USER_STATUS,
  emoji: string,
  text: string,
  expires_at: string,
  userId: string
};

// @ts-expect-error
export const refreshRedeemCode = (userId: string) => async (dispatch, getState) => {
  const { organisation: { selected } } = getState();

  const { data: code } = await Api.post(`/v1/organisations/${selected.id}/users/${userId}/redeem-code/request`);

  dispatch({
    type: CORE_REFRESH_REDEEM_CODE,
    userId,
    code,
  });
};

export type AddUserToCommunityAction = {
  type: 'core/ADD_USER_TO_COMMUNITY';
  userId: string;
  networks: NetworkScope[];
};

type ActualAddUserToCommunityAction = ThunkAction<
Promise<AddUserToCommunityAction>,
StoreState,
unknown,
AddUserToCommunityAction
>;

export const addUserToCommunity = (
  userId: string,
  network: Network,
  roles?: Array<Pick<Role, 'id'>>,
  options: { setAsPrimary?: boolean } = {},
): ActualAddUserToCommunityAction => async (dispatch, getState) => {
  const { organisation: { selected } } = getState();

  await Api.put(`/v2/organisations/${selected.id}/users/${userId}/networks`, { add: [network.id] });

  if (roles && !R.isEmpty(roles)) {
    await Api.put(`/v1/networks/${network.id}/users/${userId}/roles`, { add: roles.map(({ id }) => id) });
  }

  if (options.setAsPrimary) {
    await Api.put(
      `/v1/organisations/${selected.id}/users/${userId}/primary-community`,
      { network_id: network.id },
    );
  }

  type GetApiResponse = { data: { scopes: { networks: NetworkScope[] } } };
  const { data } = await Api.get<GetApiResponse>(`/v2/organisations/${selected.id}/users/${userId}`);

  return dispatch({
    type: 'core/ADD_USER_TO_COMMUNITY',
    userId,
    networks: data.scopes.networks,
  });
};

export type ReceiveUserAction = {
  type: typeof CORE_RECEIVE_USER;
  user: ExtendedUser;
};

export type RemoveUserFromCommunity = {
  type: 'core/REMOVE_USER_FROM_COMMUNITY';
  userId: string;
  networkId: string;
};

type ActualRemoveUserFromCommunity = ThunkAction<
Promise<ReceiveUserAction>,
StoreState,
unknown,
ReceiveUserAction
>;

export const removeUserFromCommunity = (
  userId: string,
  networkId: string,
  hasOrganisationRemovePermission: boolean,
): ActualRemoveUserFromCommunity => async (dispatch, getState) => {
  if (hasOrganisationRemovePermission) {
    const { organisation: { selected } } = getState();
    await Api.put(`/v2/organisations/${selected.id}/users/${userId}/networks`, { remove: [networkId] });
  } else {
    await Api.delete(`/v1/networks/${networkId}/users/${userId}`);
  }

  return dispatch(fetchUser(userId));
};

export type AddUserToFunctionGroup = {
  type: 'core/ADD_USER_TO_FUNCTION_GROUP';
  functionGroup: FunctionLink;
  userId: string;
};

type ActualAddUserToFunctionGroup = ThunkAction<
Promise<AddUserToFunctionGroup | ReceiveUserAction>,
StoreState,
unknown,
AddUserToFunctionGroup | ReceiveUserAction
>;

export const addUserToFunctionGroup = (
  userId: string,
  functionGroup: FunctionLink,
  networkId: string | undefined,
  options: { setAsPrimary?: boolean } = {},
): ActualAddUserToFunctionGroup => async (dispatch, getState) => {
  const { organisation: { selected } } = getState();

  const endpoint = networkId
    ? `/v2/networks/${networkId}/users/${userId}/functions`
    : `/v2/organisations/${selected.id}/users/${userId}/functions`;

  await Api.put(endpoint, { add: [functionGroup.id] });

  if (options.setAsPrimary) {
    const primaryEndpoint = networkId
      ? `/v1/networks/${networkId}/users/${userId}/primary-function-group`
      : `/v1/organisations/${selected.id}/users/${userId}/primary-function-group`;

    await Api.put(primaryEndpoint, { function_id: functionGroup.id });
  }

  return dispatch(fetchUser(userId));
};

export type RemoveUserFromFunctionGroup = {
  type: 'core/REMOVE_USER_FROM_FUNCTION_GROUP';
  functionGroupId: string;
  userId: string;
};

type ActualRemoveUserFromFunctionGroup = ThunkAction<
Promise<ReceiveUserAction>,
StoreState,
unknown,
ReceiveUserAction
>;

export const removeUserFromFunctionGroup = (
  userId: string,
  functionGroupId: string,
  networkId: string | undefined,
): ActualRemoveUserFromFunctionGroup => async (dispatch, getState) => {
  const { organisation: { selected } } = getState();

  const endpoint = networkId
    ? `/v2/networks/${networkId}/users/${userId}/functions`
    : `/v2/organisations/${selected.id}/users/${userId}/functions`;

  await Api.put(endpoint, { remove: [functionGroupId] });

  return dispatch(fetchUser(userId));
};

type ActualSetUserPrimary = ThunkAction<
Promise<ReceiveUserAction>,
StoreState,
unknown,
ReceiveUserAction
>;

export const setUserPrimaryCommunity = (
  userId: string,
  networkId: string,
): ActualSetUserPrimary => async (
  dispatch,
  getState,
) => {
  const { organisation: { selected } } = getState();

  await Api.put(
    `/v1/organisations/${selected.id}/users/${userId}/primary-community`,
    { network_id: networkId },
  );

  return dispatch(fetchUser(userId));
};

export const setUserPrimaryFunctionGroup = (
  userId: string,
  functionId: string,
  networkId?: string,
): ActualSetUserPrimary => async (
  dispatch,
  getState,
) => {
  const { organisation: { selected } } = getState();

  const endpoint = networkId
    ? `/v1/networks/${networkId}/users/${userId}/primary-function-group`
    : `/v1/organisations/${selected.id}/users/${userId}/primary-function-group`;

  await Api.put(endpoint, { function_id: functionId });

  return dispatch(fetchUser(userId));
};

export const setUserManager = (
  userId: string,
  managerUserId: string | null,
  networkId?: string,
) => async (
  dispatch: any,
  getState: () => StoreState,
) => {
  const { organisation: { selected } } = getState();

  const endpoint = networkId
    ? `/v1/networks/${networkId}/users/${userId}/manager`
    : `/v1/organisations/${selected.id}/users/${userId}/manager`;

  const { data } = await Api.put<{
    data: {
      manager: SimpleUser | null;
      manager_source: ManagerSource;
      manager_manually_set: boolean;
    };
  }>(endpoint, { manager_user_id: managerUserId });

  await dispatch(fetchUser(userId));

  return data;
};

export const dismiss = (name: string) => async (dispatch:any) => {
  Storage.setCookie(`dismiss_${name}`, true);

  dispatch({
    type: CORE_DISMISS,
    name,
  });
};

export { addMentionedUser, addRelatedUsers } from './add-user-to-store';
